import React, { useMemo, useEffect, Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import injectSaga from 'utils/injectSaga';
import { getRegionalBrandName } from 'utils/domainHelper';
import checkoutSaga from 'containers/CheckoutPage/saga';
import { CHECKOUT_PAGE_KEY } from 'containers/CheckoutPage/constants';
import { replace, push } from 'connected-react-router';
import Loader from 'components/Loader';
import { Mobile } from 'components/Media';
import { Section } from 'components/Ui';
import { selectIsCardsFetching } from 'containers/PrimoProfile/selectors';
import { Helmet } from 'react-helmet';
import MessageDialog from 'components/Dialogs/MessageDialog';
import {
  selectCartData,
  selectCartId,
  selectCartIsLoaded,
  selectContactData,
  selectIsContactsFilled,
  selectIsBillingFilled,
  selectIsDeliverySetupFilled,
  selectItemsCount,
  selectCartSummary,
} from 'containers/Cart/selectors';
import {
  selectOrderData,
  selectDeliveryData,
  selectCreatePasswordError,
  selectCreatePasswordSuccess,
} from 'containers/CheckoutPage/selectors';
import { resetCreatePassword } from 'containers/CheckoutPage/actions';
import CheckoutSummary from 'containers/PrimoCheckoutPage/Review/Summary';
import { getLocalStorageCartId } from 'containers/Cart/utils';
import { dataLayerPush } from 'utils/tracking';
import { getToUrl, getCurrencyCode, translateData } from 'utils/translation';
import { PROMO_NAME } from 'containers/QuickShop/constants';
import { selectBranch } from 'containers/UserRegion/selectors';
import SubHeader from './SubHeader';
import Footer from './Footer';
import Header from './Header';

import {
  INFORMATION_PAGE,
  REVIEW_PAGE,
  SETUP_PAGE,
  PAYMENT_PAGE,
  CONFIRMATION_PAGE,
} from './constants';
import { steps } from './steps';

import styles from './styles.scss';

const PrimoCheckoutPage = ({
  match: {
    params: { page },
  },
}) => {
  const { content: Content, subtitle, title, notes, notesOther } = useMemo(
    () => steps[page],
    [page],
  );
  const delivery = useSelector(selectDeliveryData());
  const notesToDisplay =
    page === CONFIRMATION_PAGE &&
    delivery &&
    delivery.validationMessage !== 'SUCCESS'
      ? notesOther
      : notes;

  const dispatch = useDispatch();
  const isCartFetching = useSelector(selectIsCardsFetching());
  const cartId = useSelector(selectCartId());
  const cartData = useSelector(selectCartData());
  const cartIsLoaded = useSelector(selectCartIsLoaded());
  const order = useSelector(selectOrderData());
  const isDeliveryInformationFilled = useSelector(selectIsContactsFilled());
  const isDeliverySetupFilled = useSelector(selectIsDeliverySetupFilled());
  const isPaymentInformationFilled = useSelector(selectIsBillingFilled());
  const [canGoForward, changeCanGoForward] = useState(false);
  const [submitAction, changeSubmitAction] = useState(() => () => {});
  const confirmationNumber = order ? order.webConfirmationNumber : ''; // 99999
  const cartCount = useSelector(selectItemsCount());
  const branch = useSelector(selectBranch());
  const createPasswordError = useSelector(selectCreatePasswordError());
  const createPasswordSuccess = useSelector(selectCreatePasswordSuccess());

  const handleGoBack = () => {
    const index = Object.keys(steps).indexOf(page);

    if (index > 0) {
      const prevStep = Object.keys(steps)[index - 1];

      dispatch(push(getToUrl(`/checkout/${prevStep}`, 'U42')));
    }
  };

  const handleCloseMessageDialog = () => {
    dispatch(resetCreatePassword());
  };

  const cartSummary = useSelector(selectCartSummary());
  const cartContact = useSelector(selectContactData());

  useEffect(() => {
    if (
      order &&
      order.webConfirmationNumber &&
      cartData &&
      cartData.items &&
      cartData.items.length > 0
    ) {
      const {
        tax,
        discount,
        subtotal,
        promotionId,
        campaignId,
        elementId,
      } = cartSummary;
      const { firstName = '', lastName = '', email = '' } = cartContact;
      const requestedDate = cartData.deliverySetup?.deliveryDate || '';
      const revenue = discount ? subtotal - discount : subtotal;
      const payload = {
        event: 'purchase',
        landingPageId: campaignId ? `${campaignId}_${elementId}` : '',
        requested_date: requestedDate,
        user: {
          first_name: firstName,
          last_name: lastName,
          email,
          oracle_contact_id: order.oraContactId,
        },
        ecommerce: {
          transaction_id: confirmationNumber,
          currency: getCurrencyCode(),
          affiliation: branch,
          value: `${Math.round(revenue * 100) / 100}`,
          tax: `${tax}`,
          shipping: '',
          coupon: promotionId ? PROMO_NAME : '',

          items: cartData.items.map((item) => ({
            item_id: item.itemNumber,
            item_name: item.name,
            affiliation: branch,
            item_brand: item.brand || '',
            // HINT: Category sometimes has pipe delimiter.
            ...item.category
              .split('|')
              // HINT: Supports upto 5 categories.
              .slice(0, 5)
              .reduce(
                (obj, cat, i) => ({
                  ...obj,
                  // HINT: Props are like category, category2, category3, ...
                  [`item_category${i > 0 ? i + 1 : ''}`]: cat.trim(),
                }),
                {},
              ),
            price: `${item.price}`,
            item_variant: item.size,
            quantity: item.quantity,
          })),
        },
      };
      dataLayerPush('Acquisition', payload, 'dlA34', 'PUSH_ADD_TO_PLACE_ORDER');
    }
  }, [order]);

  useEffect(() => {
    if (![INFORMATION_PAGE, CONFIRMATION_PAGE].includes(page)) {
      dispatch(replace(getToUrl(`/checkout/${INFORMATION_PAGE}`, 'U43')));
    }
  }, []);

  useEffect(() => {
    if (!cartCount >= 1 && page !== CONFIRMATION_PAGE) {
      // dispatch(replace(getToUrl('/cart', 'U44'))); // TODO: Use to go here, but why?
      dispatch(replace(getToUrl(`/checkout/${INFORMATION_PAGE}`, 'U44')));
    }
  }, [cartCount]);

  useEffect(() => {
    if (
      page !== CONFIRMATION_PAGE &&
      (!getLocalStorageCartId() ||
        (cartIsLoaded && (!cartId || isEmpty(cartData.items))))
    ) {
      dispatch(replace(getToUrl('/cart', 'U45')));
    }
  }, [cartIsLoaded, cartId, page]);

  useEffect(() => {
    if (
      [SETUP_PAGE, PAYMENT_PAGE, REVIEW_PAGE].includes(page) &&
      !isDeliveryInformationFilled
    ) {
      dispatch(replace(getToUrl(`/checkout/${INFORMATION_PAGE}`, 'U46')));
    }

    if ([PAYMENT_PAGE, REVIEW_PAGE].includes(page) && !isDeliverySetupFilled) {
      dispatch(replace(getToUrl(`/checkout/${SETUP_PAGE}`, 'U47')));
    }

    if ([REVIEW_PAGE].includes(page) && !isPaymentInformationFilled) {
      dispatch(replace(getToUrl(`/checkout/${PAYMENT_PAGE}`, 'U48')));
    }
  }, [page]);

  const siteName = getRegionalBrandName(null, 17);
  const translatedTitle = translateData(title);

  return (
    <Fragment>
      <Helmet
        title={`${translateData('Checkout Page')} ${translatedTitle.replace(
          '{site-name}',
          siteName,
        )}`}
      />
      <main
        className={styles.contentContainer}
        aria-label={`${translateData('Checkout Page')} ${page} ${translateData(
          'Main Content',
        )}`}
      >
        <Loader loading={isCartFetching}>
          <SubHeader
            order={order}
            subtitle={subtitle}
            title={title}
            notes={notesToDisplay}
            additionalNote=""
            confirmationNumber={confirmationNumber}
          />
          {createPasswordError && (
            <MessageDialog
              open
              onClose={() => handleCloseMessageDialog()}
              message={createPasswordError}
              isErrorMessage
            />
          )}
          {createPasswordSuccess && (
            <MessageDialog
              open
              title={'Success!'}
              onClose={() => handleCloseMessageDialog()}
              message={'Password successfully created'}
            />
          )}
          {page === REVIEW_PAGE && <CheckoutSummary source="cart" />}
          <Section
            className={classnames(
              styles.content,
              page === CONFIRMATION_PAGE && styles.noContainer,
            )}
          >
            <Content
              changeCanGoForward={changeCanGoForward}
              changeSubmitAction={changeSubmitAction}
            />
          </Section>
          {page === CONFIRMATION_PAGE && order && (
            <CheckoutSummary source="order" />
          )}
          <Footer additionalNote={steps[page].additionalNote} />
        </Loader>
      </main>
      <Mobile>
        <Header
          page={page}
          isForwardEnabled={canGoForward}
          forwardHandler={submitAction}
          backHandler={handleGoBack}
        />
      </Mobile>
    </Fragment>
  );
};

const withCheckoutSaga = injectSaga({
  key: CHECKOUT_PAGE_KEY,
  saga: checkoutSaga,
});

export default compose(withRouter, withCheckoutSaga)(PrimoCheckoutPage);
